import jsPDF from "jspdf";
import {
  DEFAULT_FONT,
  getLanguageFromText,
  getSegmentsText,
  specialLanguage,
  importFont,
  isInterpackInternationalProduct
} from "../common-library/DetectLanguage";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const ArabicReshaper = require("arabic-reshaper");

const ARABIC_CHAR_REGEX = /[\u0600-\u06FF]/;
const containsArabic = (text: string): boolean => ARABIC_CHAR_REGEX.test(text);
const processArabicText = (text: string): string =>
  containsArabic(text) ? ArabicReshaper.convertArabic(text) : text;

export class MultilingualText {
  doc = new jsPDF();
  text = "";
  defaultFont = DEFAULT_FONT;
  product = "";
  // Optional per-call override of the product-type-based interpack check.
  // Unset (default) preserves existing behavior for every non-CI caller
  // (receipt-v2 resi/thermal PDFs); Commercial Invoice call sites pass the
  // per-STT isInternational flag explicitly.
  isInternational?: boolean;
  startX = 0;
  startY = 0;
  maxWidth = 0;
  fontStyle = "normal";
  fontSize = 0;
  lineSpacing = 0.5;
  maxLines = 0;

  constructor(fields?: Partial<MultilingualText>) {
    Object.assign(this, fields);
  }
}
export const renderMultilingualText = async ({
  doc,
  text,
  defaultFont,
  product,
  isInternational,
  startX,
  startY,
  maxWidth,
  fontSize,
  fontStyle,
  maxLines
}: MultilingualText) => {
  const isInterpack =
    isInternational !== undefined
      ? isInternational
      : isInterpackInternationalProduct(product);

  doc.setFontSize(fontSize);

  const lineHeight = doc.getTextDimensions("A").h;

  let finalY = startY;
  let rowText = 1;

  // ===============================
  // NON INTERPACK
  // ===============================
  if (!isInterpack) {
    doc.setFont(defaultFont, fontStyle);

    let lines = doc.splitTextToSize(text, maxWidth);

    if (maxLines) {
      lines = lines.slice(0, maxLines);
    }

    doc.text(lines, startX, startY);

    finalY = startY + (lines.length - 1) * lineHeight + 1;

    return {
      endY: finalY,
      height: finalY - startY + lineHeight,
      rowText: lines.length
    };
  }

  // ===============================
  // INTERPACK (segment render)
  // ===============================
  const segments = getSegmentsText(text);

  let x = startX;
  let y = startY;

  for (const { segment, isWordLike } of segments) {
    let font = defaultFont;

    if (isWordLike) {
      // Already gated by isInterpack above (product-type or explicit
      // isInternational override) — detect the per-segment font directly
      // instead of re-deriving eligibility from product type here too.
      font = await getLanguageFromText({
        text: segment,
        product,
        jsPdf: doc
      });

      if (
        (font === DEFAULT_FONT || font === defaultFont) &&
        containsArabic(segment)
      ) {
        if (!Object.keys(doc.getFontList()).includes("Arabic")) {
          await importFont("ar", doc);
        }
        font = "Arabic";
      }
    }

    doc.setFont(font, fontStyle);
    doc.setFontSize(specialLanguage(font) ? fontSize - 0.5 : fontSize);

    const renderedText = processArabicText(segment);
    const segmentWidth = doc.getTextDimensions(segment).w;

    const willOverflow = x + segmentWidth > startX + maxWidth;

    // ===============================
    // HANDLE WRAP
    // ===============================
    if (willOverflow) {
      y += lineHeight + 0.25;
      x = startX;

      rowText++;
    }

    doc.text(renderedText, x, y);
    x += segmentWidth;

    finalY = y + 0.8;
  }

  return {
    endY: finalY,
    height: finalY - startY + lineHeight,
    rowText
  };
};
